import React, { useState, useEffect, memo } from 'react';
import { Route, useHistory, useLocation, matchPath } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { closeDialog } from '@Containers/Dialogs/DialogsActions';
import { typeOfTitlesAbstractsModal } from '@Containers/TitlesAbstractsModal/TitlesAbstractsModalConstants';
import { getCombinedRouteParams } from './AppRoutesSelectors';
import { setCombinedRouteParams } from './AppRoutesActions';
import { sourceKeyMapping, paths } from './constants';

// Compares combinedRouteParams (from Redux store) to determine if the component should update.
// Prevents re-render when path and combinedRouteParams stay the same.
const MemoizedRoute = connect(
  (state) => ({
    combinedRouteParams: getCombinedRouteParams(state),
  }),
  null
)(
  memo(
    Route,
    (prevProps, nextProps) =>
      prevProps.path === nextProps.path &&
      prevProps.combinedRouteParams === nextProps.combinedRouteParams &&
      !nextProps.path.includes(paths.RS)
  )
);

export default function ConnectedRoute(props) {
  const [isInitialRender, setIsInitialRender] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const dispatchSetCombinedRouteParams = (_location) => {
    const { state: locationState, pathname, search: searchStr } = _location;
    const match = matchPath(pathname, props);
    let trackingPathName = sourceKeyMapping[pathname] || pathname;
    if (pathname.indexOf('/results/') >= 0) {
      trackingPathName = sourceKeyMapping.results || 'main.results.context';
    }
    if (pathname.indexOf('/heatmap/') >= 0) {
      trackingPathName = sourceKeyMapping.heatmap || 'main.results.heatmap';
    }
    const routeParams = get(match, 'params', {});
    const locationSearch = Object.fromEntries(new URLSearchParams(searchStr));
    dispatch(setCombinedRouteParams({ locationState, routeParams, locationSearch, trackingPathName }));
  };

  useEffect(() => {
    // Set params for initial location
    dispatchSetCombinedRouteParams(location);
    setIsInitialRender(false);
    // tracking loadtime initiate for page change
    RouteChangeTracking.trackOnRouteChangeStart();
    dispatch(
      closeDialog({
        id: typeOfTitlesAbstractsModal,
      })
    );
    // Subscribe to history changes
    const historyUnlisten = history.listen((_location) => {
      dispatchSetCombinedRouteParams(_location);
    });

    return () => {
      historyUnlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Prevent initial render with empty params
  return isInitialRender ? null : <MemoizedRoute {...props} />;
}

export function withCombinedRouteParams(Component) {
  function WithCombinedRouteParamsHOC(props) {
    const combinedRouteParams = useSelector((state) => getCombinedRouteParams(state));
    return <Component {...props} {...combinedRouteParams} />;
  }
  return WithCombinedRouteParamsHOC;
}

export function withCombinedRouteParamsforResults(Component) {
  function WithCombinedRouteParamsforResultHOC(props) {
    let params = useSelector((state) => getCombinedRouteParams(state));
    const { match } = props;
    /** in case of browser back location.state is not persisting so we need this check */
    if (!params || Object.keys(params).length === 0) {
      params = { ...match.params };
    }
    return <Component {...props} params={params} />;
  }
  return WithCombinedRouteParamsforResultHOC;
}

export function withCombinedRouteParamsforHeatmapResults(Component) {
  function WithCombinedRouteParamsforResultHOC(props) {
    let params = useSelector((state) => getCombinedRouteParams(state));
    const { match } = props;
    /** in case of browser back location.state is not persisting so we need this check */
    if (!params || Object.keys(params).length === 0) {
      params = { ...match.params, isHeatmap: true };
    }

    return <Component {...props} params={params} />;
  }
  return WithCombinedRouteParamsforResultHOC;
}
